import { useCallback, useEffect, useState } from "react";
import { api, money, downloadCsv, formatDate, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stat, PageHeader, profitTone } from "@/components/Stat";
import { DataTable } from "@/components/DataTable";
import { sourceLabel } from "@/components/TxPanel";
import { FileSpreadsheet, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const range = { start: start || undefined, end: end || undefined };
  const load = useCallback(() => { api.get("/dashboard", { params: { start: start || undefined, end: end || undefined } }).then((r) => setData(r.data)); }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const exportSummary = () => {
    const rows = [["Bölüm", "Kalem", "Gelir", "Gider", "Kâr"]];
    data.areas.forEach((a) => rows.push(["İş Alanı", a.area, a.revenue, a.expense, a.profit]));
    data.monthly.forEach((m) => rows.push(["Aylık", m.month, m.revenue, m.expense, m.profit]));
    data.categories.forEach((c) => rows.push(["Gider Kategorisi", c.category, "", c.value, ""]));
    downloadCsv("gelir-gider-ozet.csv", rows);
    toast.success("Özet rapor indirildi");
  };

  const exportLedger = async () => {
    try {
      const all = [];
      for (let skip = 0; ; skip += 200) {
        const r = await api.get("/transactions", { params: { ...range, skip, limit: 200 } });
        all.push(...r.data.items);
        if (r.data.items.length === 0 || all.length >= r.data.total) break;
      }
      downloadCsv("finansal-hareketler.csv", [["Tarih", "Alan", "Kaynak", "Kategori", "Açıklama", "Cari", "Yön", "Tutar", "Ödenen", "Kalan", "Durum"],
        ...all.map((t) => [t.transaction_date, t.business_area, sourceLabel(t.source_type), t.category_name, t.description, t.party_name,
          t.direction, t.amount, t.paid_amount, t.remaining, t.display_status])]);
      toast.success(`${all.length} hareket indirildi`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-muted-foreground">Yükleniyor...</div>;
  const k = data.kpis;
  return (
    <div className="space-y-6" data-testid="reports-page">
      <PageHeader title="Raporlar" subtitle={`Dönem: ${start ? formatDate(start) : "başlangıç"} – ${end ? formatDate(end) : "bugün"}`}>
        <Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" />
        <Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" />
        <Button variant="outline" onClick={exportSummary} className="gap-2" data-testid="export-summary"><FileSpreadsheet className="w-4 h-4" />Özet (Excel/CSV)</Button>
        <Button variant="outline" onClick={exportLedger} className="gap-2" data-testid="export-ledger"><FileDown className="w-4 h-4" />Tüm Hareketler (CSV)</Button>
        <Button onClick={() => window.print()} className="gap-2" data-testid="export-pdf"><Printer className="w-4 h-4" />Yazdır / PDF</Button>
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Toplam Gelir" value={money(k.total_revenue)} tone="blue" testId="report-revenue" />
        <Stat label="Toplam Gider" value={money(k.total_expense)} tone="rose" testId="report-expense" />
        <Stat label="Net Kâr" value={money(k.net_profit)} tone={profitTone(k.net_profit)} testId="report-profit" />
      </div>
      <Card className="p-4"><h3 className="font-display font-semibold mb-3">İş Alanı Kâr / Zarar</h3>
        <DataTable testIdPrefix="report-area" rows={data.areas.map((a) => ({ ...a, id: a.area }))} columns={[
          { key: "area", label: "İş Alanı", className: "font-medium" },
          { key: "revenue", label: "Gelir", align: "right", render: (a) => money(a.revenue) },
          { key: "expense", label: "Gider", align: "right", render: (a) => money(a.expense) },
          { key: "profit", label: "Kâr", align: "right", render: (a) => <b className={a.profit >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(a.profit)}</b> }]} />
      </Card>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4"><h3 className="font-display font-semibold mb-3">Aylık Gelir-Gider</h3>
          <DataTable testIdPrefix="report-month" rows={data.monthly.map((m) => ({ ...m, id: m.month }))} columns={[
            { key: "month", label: "Ay", className: "font-medium" },
            { key: "revenue", label: "Gelir", align: "right", render: (m) => money(m.revenue) },
            { key: "expense", label: "Gider", align: "right", render: (m) => money(m.expense) },
            { key: "profit", label: "Kâr", align: "right", render: (m) => <b className={m.profit >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(m.profit)}</b> }]} />
        </Card>
        <Card className="p-4"><h3 className="font-display font-semibold mb-3">Gider Kategorileri</h3>
          <DataTable testIdPrefix="report-cat" rows={data.categories.map((c) => ({ ...c, id: c.category }))} columns={[
            { key: "category", label: "Kategori", className: "font-medium" },
            { key: "value", label: "Tutar", align: "right", render: (c) => money(c.value) }]} />
        </Card>
      </div>
    </div>
  );
}
